import { create } from 'zustand';
import { getTaskScheduleRules } from '../api/generated/task-schedule-rules/task-schedule-rules';
import type {
  FacilityFlowCoreModulesPlanningDtosScheduleRulesTaskScheduleRuleDto,
  FacilityFlowCoreModulesPlanningDtosScheduleRulesCreateTaskScheduleRuleRequest,
  FacilityFlowCoreModulesPlanningDtosScheduleRulesUpdateTaskScheduleRuleRequest,
} from '../api/generated/models';

const {
  getApiV1TasksTaskIdScheduleRules,
  postApiV1TasksTaskIdScheduleRules,
  putApiV1TasksTaskIdScheduleRulesRuleId,
  deleteApiV1TasksTaskIdScheduleRulesRuleId,
} = getTaskScheduleRules();

export type ScheduleRuleDto = FacilityFlowCoreModulesPlanningDtosScheduleRulesTaskScheduleRuleDto;
export type CreateScheduleRuleRequest = FacilityFlowCoreModulesPlanningDtosScheduleRulesCreateTaskScheduleRuleRequest;
export type UpdateScheduleRuleRequest = FacilityFlowCoreModulesPlanningDtosScheduleRulesUpdateTaskScheduleRuleRequest;

interface ScheduleRuleState {
  rules: ScheduleRuleDto[];
  loading: boolean;
  error: string | null;

  fetchRules: (taskId: string) => Promise<void>;
  /**
   * Rules of a task without touching the shared `rules` (other screens show those); null when
   * loading fails, so a caller never mistakes another task's rules for these.
   */
  peekRules: (taskId: string) => Promise<ScheduleRuleDto[] | null>;
  createRule: (taskId: string, data: CreateScheduleRuleRequest) => Promise<ScheduleRuleDto>;
  updateRule: (taskId: string, ruleId: string, data: UpdateScheduleRuleRequest) => Promise<ScheduleRuleDto>;
  deleteRule: (taskId: string, ruleId: string) => Promise<void>;
  clearRules: () => void;
  clearError: () => void;
}

export const useScheduleRuleStore = create<ScheduleRuleState>((set, get) => ({
  rules: [],
  loading: false,
  error: null,

  fetchRules: async (taskId: string) => {
    set({ loading: true, error: null });
    try {
      const data = await getApiV1TasksTaskIdScheduleRules(taskId);
      set({ rules: data || [], loading: false });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Laden der Wiederholungsregeln';
      set({ error: errorMessage, loading: false });
    }
  },

  peekRules: async (taskId: string) => {
    try {
      return (await getApiV1TasksTaskIdScheduleRules(taskId)) || [];
    } catch {
      return null;
    }
  },

  createRule: async (taskId: string, data: CreateScheduleRuleRequest) => {
    set({ loading: true, error: null });
    try {
      const result = await postApiV1TasksTaskIdScheduleRules(taskId, data);
      await get().fetchRules(taskId);
      set({ loading: false });
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Erstellen der Wiederholungsregel';
      set({ error: errorMessage, loading: false });
      throw err;
    }
  },

  updateRule: async (taskId: string, ruleId: string, data: UpdateScheduleRuleRequest) => {
    set({ loading: true, error: null });
    try {
      const result = await putApiV1TasksTaskIdScheduleRulesRuleId(taskId, ruleId, data);
      await get().fetchRules(taskId);
      set({ loading: false });
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Aktualisieren der Wiederholungsregel';
      set({ error: errorMessage, loading: false });
      throw err;
    }
  },

  deleteRule: async (taskId: string, ruleId: string) => {
    set({ loading: true, error: null });
    try {
      await deleteApiV1TasksTaskIdScheduleRulesRuleId(taskId, ruleId);
      await get().fetchRules(taskId);
      set({ loading: false });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Löschen der Wiederholungsregel';
      set({ error: errorMessage, loading: false });
      throw err;
    }
  },

  clearRules: () => set({ rules: [], error: null }),
  clearError: () => set({ error: null }),
}));
